import type { ClientPublic } from "@/features/clients/client-types"
import type { SelectionSheetActivityPublic } from "@/features/projects/selection-sheet/selections-types"
import type { LocationFields } from "@/lib/location-types"
import type { BaseListParams, ListEnvelope } from "@/lib/list-api"
import type { UserPublic } from "@/lib/me-api"

/** Mirrors backend `ProjectType` literal. */
export const PROJECT_TYPES = [
  "Custom Residential",
  "Production",
  "Commercial",
  "Remodel",
  "Material Only",
  "Warranty",
] as const

export type ProjectType = (typeof PROJECT_TYPES)[number]

/**
 * Gray pill for a stage label that is not one of the account's configured options (legacy value or
 * one written straight through the API). Configured stages are colored from their own `color_hex`
 * — see `features/project-options/project-option-styles.ts`.
 */
export const DEFAULT_STAGE_STYLE = {
  bg: "bg-gray-100",
  text: "text-gray-500",
  dot: "bg-gray-300",
  hover: "hover:bg-gray-200",
} as const

export type StageStyle = {
  readonly bg: string
  readonly text: string
  readonly dot: string
  readonly hover: string
}

/** Pills for project status; same shape as `StageStyle`. */
export type StatusStyle = StageStyle

/** Same idea as `DEFAULT_STAGE_STYLE`, for a status label the account no longer configures. */
export const DEFAULT_STATUS_STYLE = {
  bg: "bg-gray-100",
  text: "text-gray-600",
  dot: "bg-gray-400",
  hover: "hover:bg-gray-200",
} as const

/** Project priority: fixed set of three values, styled like stage/status pills. */
export const PRIORITY_CONFIG = {
  Low: {
    bg: "bg-gray-100",
    text: "text-gray-600",
    dot: "bg-gray-400",
    hover: "hover:bg-gray-200",
  },
  Medium: {
    bg: "bg-amber-100",
    text: "text-amber-700",
    dot: "bg-amber-400",
    hover: "hover:bg-amber-200",
  },
  High: {
    bg: "bg-red-100",
    text: "text-red-700",
    dot: "bg-red-400",
    hover: "hover:bg-red-200",
  },
} as const

export const DEFAULT_PRIORITIES = ["Low", "Medium", "High"] as const

export type ProjectPriority = (typeof DEFAULT_PRIORITIES)[number]

export const DEFAULT_PRIORITY_STYLE = {
  bg: "bg-gray-100",
  text: "text-gray-600",
  dot: "bg-gray-400",
  hover: "hover:bg-gray-200",
} as const

export function getPriorityStyle(p: string): StageStyle {
  const known = PRIORITY_CONFIG as unknown as Record<string, StageStyle>
  return (known[p] ?? DEFAULT_PRIORITY_STYLE) as StageStyle
}

/**
 * Fixed pill width per chip type so every chip in a column shares one width. Stage and status labels
 * are now account-defined and arbitrary, so anything longer than the width truncates with "…" (the
 * chip carries a `title` with the full label); priority stays a fixed three-value set. Literal class
 * strings live here so Tailwind generates them; pair with `justify-center` on the chip. Keep the
 * dashboard column widths in sync: xl+ uses the larger size; lg→xl uses a smaller size so chips
 * truncate rather than overflow their column. max-w-full ensures the pill never exceeds the cell.
 */
export const STAGE_CHIP_WIDTH = "w-40 max-w-full"
export const STATUS_CHIP_WIDTH = "w-32 max-w-full"
export const PRIORITY_CHIP_WIDTH = "w-14 xl:w-16 max-w-full"
// Sized to the longest project type label ("Custom Residential") plus its leading icon, so the chip
// width never changes across types.
export const PROJECT_TYPE_CHIP_WIDTH = "w-44 max-w-full"

/** One user assigned to a project (join row + nested user). */
export type ProjectMemberPublic = {
  id: number
  project_id: number
  user: UserPublic
  created_at: string | null
  /**
   * Assumed contract addition for chat: the UserAccount (membership) id, matching
   * `ChatMessage.sender.user_account_id` / `mentioned_user_account_ids` and `MembershipPublic.id`.
   * Not documented in the original API contract — coordinate with backend so
   * `GET /api/projects` embeds this on each member row, or chat mentions can't resolve it.
   */
  user_account_id: number
}

/** Row shape from GET `/api/projects` items (ISO date strings in JSON). */
export type ProjectPublic = {
  id: number
  name: string
  phone: string | null
  email: string | null
  project_type: ProjectType
  /** Builder/developer lot number; project-specific, not part of the address. */
  lot_number: string | null
  /** Pipeline stage: one of the account's configured stage names, or null when unset. */
  stage: string | null
  /** Workflow status: one of the account's configured status names, or null when unset. */
  status: string | null
  /** Work priority: Low, Medium, or High; null = "Select" (unset). */
  priority: string | null
  /** 0-based position within this project's group (dashboard ordering). */
  position: number
  /** Board the project sits on; null only for rows written before boards existed. */
  board_id: number | null
  /** Group within that board; null only for rows written before groups existed. */
  group_id: number | null
  client_id: number | null
  /** Embedded when the client row exists (list and detail). */
  client: ClientPublic | null
  account_id: number
  created_at: string | null
  updated_at: string | null
  archived_at: string | null
  /** Account team members assigned to this project. */
  members: ProjectMemberPublic[]
  /** Required selection-sheet fields filled vs total (server-computed); null if no sheet / no work. */
  selection_completion: { filled: number; total: number } | null
  /** Latest selection-sheet activity rows (GET project detail only; list endpoint returns []). */
  selection_activity_preview: SelectionSheetActivityPublic[]
} & LocationFields

/** POST `/api/projects/` body (snake_case). */
export type ProjectCreateBody = {
  name: string
  client_id: number
  /** Board to create on; omitted means the account's default board. */
  board_id?: number | null
  project_type: ProjectType
  lot_number: string | null
  phone: string | null
  email: string | null
} & LocationFields

/** PATCH `/api/projects/{id}` body (all fields optional; send only what changes). */
export type ProjectUpdateBody = {
  name?: string
  lot_number?: string | null
  phone?: string | null
  email?: string | null
  project_type?: ProjectType
  client_id?: number | null
  stage?: string | null
  status?: string | null
  priority?: string | null
} & Partial<LocationFields>

/** POST `/api/projects/reorder` body. Positions are per group (one table per group). */
export type ProjectReorderBody = {
  /** The checked rows to move, as one block. They must all sit in the same group. */
  project_ids: number[]
  /** New 0-based position among the rows that stayed put, within that group. */
  position: number
}

export type ProjectListResponse = ListEnvelope<ProjectPublic>

/** Query params sent to `GET /api/projects`. */
export type ProjectsListQuery = BaseListParams & {
  project_type?: ProjectType
  include_archived: boolean
  archived_only: boolean
  /** Restrict to one board; omitted means every board. */
  board_id?: number
}

/** Counts of project-owned rows a permanent delete will destroy (GET /delete-preview). */
export type ProjectDeletePreview = {
  selection_sheets: number
  sheet_areas: number
  sheet_templates: number
  selection_sets: number
  selection_answers: number
  activities: number
}
